import React, { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { appOrigin, APP_PATH_PREFIXES } from "./config";
import { isLive } from "./config/liveRoutes";
import { getCompetePage } from "./data/competePages";
import { useMountEffect } from "./hooks/useMountEffect";
import "./App.css";

// Context Providers — marketing-only subset. The app-only providers
// (Auth/Boot/Limits/ViewMode/Loading/LimitExceeded/GoogleOAuth) are not mounted
// here; nothing in the marketing route graph consumes them.
import { ToastProvider } from "./context/ToastContext";
import { ThemeProvider } from "./context/ThemeContext";
import { CookieConsentProvider } from "./context/CookieConsentContext";

// Components
import ErrorBoundary from "./components/ErrorBoundary";

// Both market homepages stay eager — '/' is the LCP-critical public entry point
// on either domain, and a lazy chunk there costs a round trip before first
// paint. The market is resolved at runtime (see src/config/markets.js), so
// webpack cannot fold the ternary below and each build carries the other
// market's homepage as dead weight in its main chunk. That is the deliberate
// price of not making either '/' lazy; NgHomePage is the smaller of the two,
// and BookDemoModal already pulls its heaviest dependency (the phone input)
// into the main chunk regardless.
import HomePageDusk from "./pages/HomePageDusk";
import NgHomePage from "./pages/ng/NgHomePage";

// This is the MARKETING site (comtev.com). It ships ONLY marketing routes;
// the authenticated app + auth pages + public token links live on the app
// origin (app.comtev.com) and are served by the separate `comtev-frontend`
// build. Any app/token path hit here is bounced to the app origin by the
// catch-all below (see AppPathRedirect).
//
// NOTE (seed): the provider tree below is still the full app tree, inherited
// from comtev-frontend. It renders marketing correctly today (comtev.com runs
// this exact tree), so it is kept as-is to make the seed buildable with zero
// risk of a missing-provider runtime error. Trimming it to
// Theme + Query + Toast + CookieConsent (dropping Auth/Boot/Limits/ViewMode/
// GoogleOAuth) and pruning the now-unused app deps is a tracked follow-up.
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const UseCasePage = lazy(() => import("./pages/UseCasePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
// The global '/planners' route (unlinked, unprerendered) — no longer the NG
// homepage, so it no longer needs to be eager. See the NG fork notes below.
const PlannersPage = lazy(() => import("./pages/PlannersPage"));
const BlogIndexPage = lazy(() => import("./pages/BlogIndexPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const CompetePage = lazy(() => import("./pages/CompetePage"));

/* The compete pages are static routes over src/data/competePages.ts. A missing
   record is a build-time mistake, not a user-facing 404, so fail loudly. */
function competePage(slug: string) {
  const page = getCompetePage(slug);
  if (!page) throw new Error(`No compete page record for slug "${slug}"`);
  return page;
}
const UseCasesPage = lazy(() => import("./pages/UseCasesPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ScenarioPage = lazy(() => import("./pages/ScenarioPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PersonalPage = lazy(() => import("./pages/PersonalPage"));
// PlannersPage is NOT lazy — it is the eager homepage on the ng build. See the
// HomePage fork below.
const TermsPage = lazy(() => import("./pages/TermsPage"));
// NG-only (see the howItWorks entry in src/config/siteRoutes.js). Lazy because
// it is a secondary page on that market and never the entry point.
const HowItWorksPage = lazy(() => import("./pages/ng/HowItWorksPage"));
const NgInvoiceGeneratorPage = lazy(() => import("./pages/ng/NgInvoiceGeneratorPage"));
// The NG renderings of the shared routes. Nigeria's rebrand (the `ngl` design
// system, src/styles/ng.css) replaces the corporate chrome these routes used
// to inherit; comtev.com keeps the original components. Lazy on both markets.
const NgPricingPage = lazy(() => import("./pages/ng/NgPricingPage"));
const NgAboutPage = lazy(() => import("./pages/ng/NgAboutPage"));
const NgContactPage = lazy(() => import("./pages/ng/NgContactPage"));
const NgBlogIndexPage = lazy(() => import("./pages/ng/NgBlogIndexPage"));
const NgBlogArticlePage = lazy(() => import("./pages/ng/NgBlogArticlePage"));
const AiEventPlannerUkPage = lazy(() => import("./pages/AiEventPlannerUkPage"));
const CorporateEventCostUkPage = lazy(() => import("./pages/CorporateEventCostUkPage"));
const BestAiEventToolsUkPage = lazy(() => import("./pages/BestAiEventToolsUkPage"));

// Minimal Suspense fallback — no spinner, no layout shift. The cream
// background matches the landing-page critical CSS so the fallback is
// visually invisible on `/` and unobtrusive on every other route.
const RouteFallback: React.FC = () => (
  <div aria-hidden="true" style={{ minHeight: "100vh", background: "transparent" }} />
);

// Catch-all: an app/token path reached the marketing origin (stale bookmark or
// an emailed link before the edge 301 is live). Bounce it to the app origin,
// preserving path + query; anything else is a genuine marketing 404. `appOrigin`
// is '' only when already same-origin as the app, in which case we cannot help
// and fall through to the 404.
const AppPathRedirect: React.FC = () => {
  const location = useLocation();
  const isAppPath = APP_PATH_PREFIXES.some((p) => location.pathname.startsWith(p));

  useMountEffect(() => {
    if (isAppPath && appOrigin) {
      window.location.replace(`${appOrigin}${location.pathname}${location.search}`);
    }
  });

  // While the browser navigates away, render nothing to avoid flashing the 404.
  if (isAppPath && appOrigin) return <RouteFallback />;
  return <NotFoundPage />;
};

const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <Router>
            <CookieConsentProvider>{children}</CookieConsentProvider>
          </Router>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

// This JSX table stays the source of truth for COMPONENT WIRING (which element
// answers which path — see src/constants/routes.ts); the registry in
// src/config/siteRoutes.js is the source of truth for WHICH of those routes
// exist in this market's build, read here via `isLive`. Gating rather than
// deriving the table from the registry keeps the lazy() boundaries and the
// element types statically visible, which is what makes the code-split points
// reviewable.
//
// A route the active market does not carry is simply never registered, so it
// falls through to the catch-all and renders NotFoundPage. That is deliberate:
// a 404 is the correct signal for Google to drop a URL that must not exist on
// that domain. Redirecting excluded routes to "/" would launder them into soft
// 200s and keep them indexed.

// The one real per-market fork in the table. Nigeria leads with the
// planner-reseller offering, so "/" serves the ₦-denominated, WhatsApp-first
// PlannersPage there while comtev.com keeps the corporate landing (HomePageDusk,
// the Dusk design system — see /DESIGN.md. LandingPageV2 is no longer routed;
// the file stays for reference).
//
// NG has NO "/planners" route: serving the same component at both paths would
// put two identical self-canonical pages on a brand-new domain. On comtev.com
// "/planners" stays exactly as it is today — resolvable but unlinked and out of
// the sitemap. See the planners entry in src/config/siteRoutes.js.
//
// Read straight from process.env rather than the resolved `marketId` so CRA's
// DefinePlugin inlines the comparison and the branch folds at build time.
//
// Nigeria's '/' is NgHomePage (the ngl rebrand); PlannersPage remains only as
// the global unlinked '/planners' route. Pricing, about and contact fork the
// same way: the shared corporate components keep serving comtev.com, the ng/
// components serve comtev.ng.
const HomePage = process.env.REACT_APP_MARKET === "ng" ? NgHomePage : HomePageDusk;
const MarketPricingPage = process.env.REACT_APP_MARKET === "ng" ? NgPricingPage : PricingPage;
const MarketAboutPage = process.env.REACT_APP_MARKET === "ng" ? NgAboutPage : AboutPage;
const MarketContactPage = process.env.REACT_APP_MARKET === "ng" ? NgContactPage : ContactPage;
const MarketBlogIndexPage = process.env.REACT_APP_MARKET === "ng" ? NgBlogIndexPage : BlogIndexPage;
const MarketBlogArticlePage = process.env.REACT_APP_MARKET === "ng" ? NgBlogArticlePage : BlogArticlePage;

const AppRoutes: React.FC = () => (
  <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Marketing / public routes only, market-gated against the registry. */}
      {isLive("home") && <Route path="/" element={<HomePage />} />}
      {/* Legacy landing aliases → consolidate to the single canonical "/".
          Not registry routes: they are redirects, never indexed on either
          market, so they ship on both. */}
      <Route path="/home-v2" element={<Navigate to="/" replace />} />
      <Route path="/home-v1" element={<Navigate to="/" replace />} />
      {isLive("privacy") && <Route path="/privacy" element={<PrivacyPolicyPage />} />}
      {isLive("terms") && <Route path="/terms" element={<TermsPage />} />}
      {isLive("about") && <Route path="/about" element={<MarketAboutPage />} />}
      {isLive("contact") && <Route path="/contact" element={<MarketContactPage />} />}
      {isLive("features") && <Route path="/features" element={<FeaturesPage />} />}
      {isLive("persona") && <Route path="/for/:slug" element={<UseCasePage />} />}
      {isLive("pricing") && <Route path="/pricing" element={<MarketPricingPage />} />}
      {isLive("blogIndex") && <Route path="/blog" element={<MarketBlogIndexPage />} />}
      {isLive("blogArticle") && <Route path="/blog/:slug" element={<MarketBlogArticlePage />} />}
      {isLive("useCases") && <Route path="/use-cases" element={<UseCasesPage />} />}
      {isLive("scenario") && <Route path="/use-cases/:slug" element={<ScenarioPage />} />}
      {isLive("products") && <Route path="/products" element={<ProductsPage />} />}
      {isLive("product") && <Route path="/products/:slug" element={<ProductPage />} />}
      {isLive("partners") && <Route path="/partners" element={<PartnersPage />} />}
      {isLive("personal") && <Route path="/personal" element={<PersonalPage />} />}
      {isLive("planners") && <Route path="/planners" element={<PlannersPage />} />}
      {isLive("howItWorks") && <Route path="/how-it-works" element={<HowItWorksPage />} />}
      {isLive("invoiceGenerator") && (
        <Route path="/invoice-generator" element={<NgInvoiceGeneratorPage />} />
      )}
      {isLive("aiEventPlannerUk") && (
        <Route path="/ai-event-planner-uk" element={<AiEventPlannerUkPage />} />
      )}
      {isLive("corporateEventCostUk") && (
        <Route path="/corporate-event-cost-uk" element={<CorporateEventCostUkPage />} />
      )}
      {isLive("bestAiEventToolsUk") && (
        <Route path="/best-ai-event-planning-tools-uk" element={<BestAiEventToolsUkPage />} />
      )}
      {isLive("compare") && <Route path="/compare/:slug" element={<ComparePage />} />}
      {isLive("venueFinder") && (
        <Route path="/venue-finder" element={<CompetePage page={competePage("venue-finder")} />} />
      )}
      {isLive("venueFinderLondon") && (
        <Route
          path="/venue-finder/london"
          element={<CompetePage page={competePage("venue-finder-london")} />}
        />
      )}
      {isLive("corporateEventPlanner") && (
        <Route
          path="/corporate-event-planner"
          element={<CompetePage page={competePage("corporate-event-planner")} />}
        />
      )}
      {isLive("eventManagementCompany") && (
        <Route
          path="/event-management-company"
          element={<CompetePage page={competePage("event-management-company")} />}
        />
      )}

      {/* App/token paths → bounce to the app origin; else marketing 404. */}
      <Route path="*" element={<AppPathRedirect />} />
    </Routes>
  </Suspense>
);

function App() {
  return (
    <AppProviders>
      <AppRoutes />
    </AppProviders>
  );
}

export default App;
